import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { ArrowUpRight } from "lucide-react";

export default function History() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/history");
                setItems(data);
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-6xl mx-auto px-6 py-10">
                <div className="lk-label">History</div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight">Every post you've generated</h1>
                <p className="text-[var(--lk-muted)] mt-3 max-w-xl">Full history of your prompts, generated drafts, and per-agent traces.</p>

                {loading ? (
                    <div className="mt-10 text-[var(--lk-muted)]" data-testid="history-loading">Loading…</div>
                ) : err ? (
                    <div className="mt-10 text-red-400" data-testid="history-error">{err}</div>
                ) : items.length === 0 ? (
                    <div className="mt-16 lk-border lk-surface rounded-xl p-10 text-center" data-testid="history-empty">
                        <div className="font-heading text-2xl">No generations yet</div>
                        <div className="text-[var(--lk-muted)] mt-2 text-sm">Head to the workspace and try your first prompt.</div>
                        <Link to="/workspace" className="lk-btn-primary rounded-full px-6 py-3 mt-6 inline-block" data-testid="history-cta">Open workspace</Link>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 gap-4">
                        {items.map((it) => (
                            <Link
                                key={it.id}
                                to={`/history/${it.id}`}
                                className="lk-border lk-surface rounded-xl p-5 hover:border-[var(--lk-primary)]/60 flex items-center justify-between group"
                                data-testid={`history-item-${it.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="text-xs text-[var(--lk-muted)] font-mono">
                                        {formatDistanceToNow(new Date(it.created_at), { addSuffix: true })} · {it.status}
                                    </div>
                                    <div className="mt-1 font-heading text-xl truncate">{it.prompt}</div>
                                    <div className="mt-1 text-sm text-[var(--lk-muted)] truncate max-w-2xl">{(it.hook || it.response || "").slice(0, 140)}</div>
                                </div>
                                <div className="flex items-center gap-6 shrink-0">
                                    <div className="hidden sm:block text-right">
                                        <div className="lk-label">Latency</div>
                                        <div className="font-mono text-[var(--lk-primary)] text-sm">{Math.round(it.total_latency_ms)} ms</div>
                                    </div>
                                    <div className="hidden sm:block text-right">
                                        <div className="lk-label">Score</div>
                                        <div className="font-mono text-[var(--lk-primary)] text-sm">{it.quality_score != null ? Math.round(it.quality_score) : "—"}</div>
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-[var(--lk-muted)] group-hover:text-[var(--lk-primary)]" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
